import { BusinessPaymentMethod } from "Hooks/Api/apiBusiness";
import ComboboxSetter from "../Combobox/ComboboxSetter";
import { AlertPaymentMethodNotfound } from "../Alerts/AlertPaymentMethodNotFound";
import { BusinessEntity } from "Models/Entities";
import { store } from "Models/Store";
import classNames from "classnames";
import { useApiBusinessPayments } from "Hooks/ApiHooks/useApiBusiness";
import { useEffect } from "react";

// Different text to show depending on whether we charge consumption cost or not
function getOptionDisplay(type: DisplayType, value: BusinessPaymentMethod) {
	if (type === 'INVOICED') return value.businessName;
	if (value.paymentMethodId === null) return `${value.businessName} - Payment method not attached`;
	return value.businessName;
}

function getOptionDisabled(type: DisplayType, value: BusinessPaymentMethod) {
	if (type === 'INVOICED') return false;
	return value.paymentMethodId === null;
}

function mapToOptions(type: DisplayType, data: BusinessPaymentMethod[]) {

	return data
		.filter(el => BusinessEntity.IsLinkedToUserById(el.businessId))
		.sort((a, b) => a.businessName.localeCompare(b.businessName))
		.map(el => {
			const display = getOptionDisplay(type, el);
			const clzNames = getOptionDisabled(type, el) ? ["business-option-disabled"] : [];
			return {
				display: <span className={classNames(clzNames)}>{display}</span>,
				value: el,
			}
		});
}


type DisplayType = 'INVOICED' | 'PAYG'

export type BusinessPaymentSelectorProps = {
	value: string | undefined,
	setValue: (value: BusinessPaymentMethod) => void;

	// Note, with some workflows an organisation with 'invoiced' that requires upfront payment should pass in PAYG
	// For instance, generating credit with enforced payments
	type: DisplayType,

	// For super users to filter business 
	filterByOrgId?: string;
	// For workflows that require monitor product to be enabled
	filterForEnabledMonitor?: boolean;
	// For workflows that require approve product to be enabled
	filterForEnabledApprove?: boolean;
	// Error message
	error?: string;
	isDisabled?: boolean;
}
/**
 * Selector for business with payments.
 * Pass in BusinessEntity.Id
 * Return value on the setter is the business payment model which has the selected business id and the payment method id if its definede.
 */
export function BusinessPaymentSelector(props: BusinessPaymentSelectorProps) {
	const {
		value,
		setValue,
		type,
		filterByOrgId,
		filterForEnabledMonitor,
		filterForEnabledApprove,
		error,
		isDisabled
	} = props;

	const {
		data: businessPaymentMethods,
		type: reqBusinessPaymentMethodStatus,
		refresh: refreshBusinessPaymentMethods
	} = useApiBusinessPayments(filterByOrgId, filterForEnabledMonitor, filterForEnabledApprove);

	// 1. Refresh when org id changes so we can update the business options
	useEffect(() => {
		if (filterByOrgId) refreshBusinessPaymentMethods();
	}, [filterByOrgId]);

	// 2. If theres only 1 selectable element, click it
	useEffect(() => {

		if (reqBusinessPaymentMethodStatus === "data") {
			const parsed = (businessPaymentMethods ?? []).filter(el => BusinessEntity.IsLinkedToUserById(el.businessId))
			if (parsed.length === 1) setValue(parsed[0]);
		}
	}, [businessPaymentMethods, reqBusinessPaymentMethodStatus])

	const optionData = businessPaymentMethods ?? [];
	const options = mapToOptions(type, optionData);
	const selectedOption = optionData.find(option => option.businessId === value);

	const selectedValueHasNoPayment = type === 'INVOICED' ? false : selectedOption?.paymentMethodId === null;
	const label = type === 'INVOICED' ? 'Business Entity' : 'Business Entity to bill to'
	const placeholder = type === 'INVOICED' ? 'Select Business Entity' : 'Select Business Entity to bill to'

	return (
		<div className="business-payment-selector">
			<ComboboxSetter
				isDisabled={reqBusinessPaymentMethodStatus === 'loading' || isDisabled}
				loading={reqBusinessPaymentMethodStatus === 'loading'}
				value={selectedOption}
				setValue={setValue}
				getOptionValue={optValue => optValue?.businessId}
				placeholder={placeholder}
				label={label}
				searchable
				errors={error}
				options={options}
				isRequired
			/>

			{value && selectedValueHasNoPayment && (
				<AlertPaymentMethodNotfound
					viewType={{
						type: store.userType === 'SUPER_USER' ? 'Client' : 'Customer',
						businessId: value,
						organisationId: filterByOrgId ?? "",
					}}
					userType={store.userType} />
			)}
		</div>
	)
}